import { InputWrapper, SelectWrapper } from "@/src/components/tasks/input-wrapper";
import { EntityMatcher } from "@/src/components/tasks/intake-sections/matchers";
import { Hyperlink } from "@/src/components/typography";
import { EnabledFields } from "@/src/lib/schema-types";
import { stateOptions } from "@/src/lib/us-states";
import { enumToOptions, getNestedValue, isFieldEnabled } from "@/src/lib/utils/object-utils";
import { FacilityType } from "@/src/models/constants/facility-types";
import { type Location, type LocationErrors, type LocationFieldKey, LocationRole } from "@/src/models/location";
import type { ExtractionMetadata } from "@/src/models/section";

interface LocationFormProps {
  // Base props
  location: Location;
  onChange: (field: LocationFieldKey, value: string) => void;
  createMode?: boolean;

  errors?: LocationErrors;

  // location-section props
  matchedLocation?: Location | null;
  locationObjString?: string;
  requiredFields?: string[];
  conflictingFields?: string[];
  enabledFields?: EnabledFields;
  onSave?: () => void;
  onFocus?: (metadata?: ExtractionMetadata | null) => void;
  onFlagForInaccuracyUpdate?: (key: string, value: string | null | undefined) => void;
  metadata?: unknown;

  // LocationMatcher props
  locations?: Location[];
  isLocationsLoading?: boolean;
  onLocationSelected?: (location: Location | null) => void;
  showLocationMatcher?: boolean;
  onEmptyToggle?: () => void;
}

export default function LocationForm({
  location,
  onChange,
  createMode = false,
  errors = {},
  matchedLocation,
  locationObjString,
  requiredFields = [],
  conflictingFields = [],
  enabledFields,
  onSave,
  onFocus,
  onFlagForInaccuracyUpdate,
  metadata,
  locations,
  isLocationsLoading,
  onLocationSelected,
  showLocationMatcher = false,
  onEmptyToggle,
}: LocationFormProps) {
  const handleFieldChange = (fieldKey: LocationFieldKey, value: string) => {
    onChange(fieldKey, value);
  };

  const getFieldMetadata = (fieldKey: LocationFieldKey) => {
    return getNestedValue(metadata, fieldKey + "_metadata");
  };

  const renderField = ({
    fieldKey,
    label,
    type = "text",
    options,
    placeholder,
    readonly = false,
  }: {
    fieldKey: LocationFieldKey;
    label: string;
    type?: "text" | "email" | "tel" | "select";
    options?: { value: string; label: string }[];
    placeholder?: string;
    readonly?: boolean;
  }) => {
    const value = getNestedValue(location, fieldKey) ?? "";
    const secondaryValue = getNestedValue(matchedLocation, fieldKey) ?? "";
    const error = getNestedValue(errors, fieldKey) ?? null;

    const fullFieldKey = locationObjString ? `${locationObjString}.${fieldKey}` : fieldKey;
    const isRequired = requiredFields.includes(fullFieldKey);
    const doesConflict = conflictingFields.includes(fieldKey);

    if (type === "select" && options) {
      return (
        <div key={fieldKey}>
          <SelectWrapper
            value={value}
            secondaryValue={secondaryValue}
            onChange={(newValue) => handleFieldChange(fieldKey, newValue)}
            label={label}
            disabled={readonly}
            placeholder={placeholder}
            options={options}
            onFocus={() => onFocus?.(getFieldMetadata(fieldKey))}
            onBlur={onSave}
            doesConflict={doesConflict}
            onFlagForInaccuracyUpdate={onFlagForInaccuracyUpdate}
            fieldKey={fullFieldKey}
          />
          {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
        </div>
      );
    }

    return (
      <div key={fieldKey}>
        <InputWrapper
          type={type}
          value={value}
          secondaryValue={secondaryValue}
          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
          label={label}
          placeholder={placeholder}
          isRequired={isRequired}
          onSave={onSave}
          readOnly={readonly}
          onFocus={() => onFocus?.(getFieldMetadata(fieldKey))}
          doesConflict={doesConflict}
          onFlagForInaccuracyUpdate={onFlagForInaccuracyUpdate}
          fieldKey={fullFieldKey}
          error={error}
        />
      </div>
    );
  };

  // Check if any location info fields are enabled
  const hasLocationInfoEnabled = ["name", "phoneNumber", "faxPhoneNumber", "ehrId", "facilityType"].some((field) =>
    isFieldEnabled(enabledFields, `${locationObjString}.${field}`)
  );

  // Check if role field is enabled (only relevant in create mode)
  const hasRoleEnabled = createMode && isFieldEnabled(enabledFields, `${locationObjString}.role`);

  // Check if any address fields are enabled
  const hasAddressInfoEnabled = [
    "address.line1",
    "address.line2",
    "address.city",
    "address.state",
    "address.postalCode",
    "address.country",
  ].some((field) => isFieldEnabled(enabledFields, `${locationObjString}.${field}`));

  return (
    <div className="space-y-6">
      {/* LocationMatcher Section */}
      {(showLocationMatcher || createMode) && onLocationSelected && (
        <div>
          <div className="flex justify-center items-center mb-2 gap-2">
            <div className="w-[70%]">
              <EntityMatcher
                type="location"
                existingEntities={createMode ? [] : locations}
                isLoadingExistingEntities={createMode ? false : isLocationsLoading}
                entity={matchedLocation || null}
                onEntitySelected={onLocationSelected}
              />
            </div>
            {onEmptyToggle && (
              <div className="flex items-center gap-2">
                <Hyperlink onClick={onEmptyToggle} href="#" className="text-xs">
                  Empty
                </Hyperlink>
              </div>
            )}
          </div>
          {/* Display placeId error (creating duplicate location) if it exists */}
          {errors.placeId && <p className="text-xs text-red-500 text-center">{errors.placeId}</p>}
        </div>
      )}

      {/* Location Information Section */}
      {hasLocationInfoEnabled && (
        <div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">{renderField({ fieldKey: "name", label: "Location Name" })}</div>
            <div className="col-span-6">
              {renderField({
                fieldKey: "phoneNumber",
                label: "Phone Number",
                type: "tel",
                placeholder: "555-123-4567",
              })}
            </div>
            <div className="col-span-6">
              {renderField({
                fieldKey: "faxPhoneNumber",
                label: "Fax Number",
                type: "tel",
                placeholder: "555-123-4567",
              })}
            </div>
            <div className="col-span-6">
              {renderField({
                fieldKey: "ehrId",
                label: "EHR ID",
              })}
            </div>
            <div className="col-span-6">
              {renderField({
                fieldKey: "facilityType",
                label: "Facility Type",
                type: "select",
                options: enumToOptions(FacilityType),
              })}
            </div>
          </div>
        </div>
      )}

      {/* Address Information Section */}
      {hasAddressInfoEnabled && (
        <div>
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Address Information</h3>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              {renderField({
                fieldKey: "address.line1",
                label: "Street Address",
                placeholder: "123 Main St",
                readonly: true,
              })}
            </div>
            <div className="col-span-12">
              {renderField({
                fieldKey: "address.line2",
                label: "Street Address Line 2",
                placeholder: "Apt 100, Suite 200, etc.",
                readonly: true,
              })}
            </div>
            <div className="col-span-4">{renderField({ fieldKey: "address.city", label: "City", readonly: true })}</div>
            <div className="col-span-4">
              {renderField({
                fieldKey: "address.state",
                label: "State",
                type: "select",
                options: stateOptions,
                placeholder: "Select a state",
                readonly: true,
              })}
            </div>
            <div className="col-span-4">
              {renderField({ fieldKey: "address.postalCode", label: "Zip Code", placeholder: "12345", readonly: true })}
            </div>
            <div className="col-span-12">
              {renderField({ fieldKey: "address.country", label: "Country", placeholder: "USA", readonly: true })}
            </div>
          </div>
          {/* Only render role select when creating a new location */}
          {hasRoleEnabled && (
            <div className="grid grid-cols-1 gap-4 mt-4">
              {renderField({
                fieldKey: "role",
                label: "Role",
                type: "select",
                options: enumToOptions(LocationRole),
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
